import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { INDUSTRIES, FUNCTIONS, PHASES } from "@/lib/coach-options";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import type { CoachRow } from "@/lib/coaches.types";

export const Route = createFileRoute("/coach-matchning")({
  component: MatchPage,
});

type Match = { coach: CoachRow; score: number; reason: string };

function MatchPage() {
  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState("");
  const [phase, setPhase] = useState("");
  const [funcs, setFuncs] = useState<string[]>([]);
  const [needs, setNeeds] = useState("");
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState<Match[] | null>(null);

  async function run() {
    if (!needs.trim() && !industry && !phase && funcs.length === 0) {
      toast.error("Fyll i åtminstone bransch, fas, funktioner eller behov.");
      return;
    }
    setLoading(true);
    setMatches(null);
    try {
      const { data, error } = await supabase.functions.invoke("coach-match", {
        body: { company_name: companyName, industry, phase, functions: funcs, needs },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setMatches(data?.matches ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kunde inte matcha.");
    } finally {
      setLoading(false);
    }
  }

  function toggle(arr: string[], v: string, set: (x: string[]) => void) {
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  }

  return (
    <AppShell>
      <section className="px-8 pt-10 pb-16 max-w-5xl">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Coachmatchning</div>
          <h1 className="font-display text-4xl">AI-driven matchning</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-2xl">
            Beskriv bolagets behov så föreslår vi de coacher i databasen som passar bäst, med motivering.
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 space-y-5 mb-8">
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Bolagsnamn</Label><Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="t.ex. Acme AB" /></div>
            <div>
              <Label>Bransch</Label>
              <select value={industry} onChange={(e) => setIndustry(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm">
                <option value="">— Välj —</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label>Affärsfas</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {PHASES.map((p) => (
                <button key={p} type="button" onClick={() => setPhase(phase === p ? "" : p)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${phase === p ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}>{p}</button>
              ))}
            </div>
          </div>

          <div>
            <Label>Funktionella behov</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {FUNCTIONS.map((f) => {
                const a = funcs.includes(f);
                return (
                  <button key={f} type="button" onClick={() => toggle(funcs, f, setFuncs)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${a ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}>{f}</button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Specifika behov / kontext</Label>
            <Textarea rows={4} value={needs} onChange={(e) => setNeeds(e.target.value)}
              placeholder="t.ex. behöver hjälp med kapitalanskaffning seed-runda, B2B SaaS, internationalisering till Tyskland…" />
          </div>

          <Button onClick={run} disabled={loading} className="w-full sm:w-auto">
            {loading ? <><Loader2 className="size-4 mr-2 animate-spin" />Matchar…</> : <><Sparkles className="size-4 mr-2" />Hitta matchande coacher</>}
          </Button>
        </div>

        {matches && (
          <div>
            <h2 className="font-display text-2xl mb-4">Förslag</h2>
            {matches.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
                Inga matchningar hittades. Lägg till fler aktiva coacher eller justera behoven.
              </div>
            ) : (
              <div className="space-y-3">
                {matches.map((m) => (
                  <Link key={m.coach.id} to="/coacher/$id" params={{ id: m.coach.id }}
                    className="block rounded-2xl border border-border bg-card p-5 hover:border-foreground/30 transition-colors">
                    <div className="flex items-start gap-4">
                      {m.coach.photo_url ? (
                        <img src={m.coach.photo_url} alt="" className="size-14 rounded-full object-cover" />
                      ) : (
                        <div className="size-14 rounded-full bg-secondary" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium">{m.coach.full_name}</div>
                          <Badge>{Math.round(m.score)}/100</Badge>
                          {m.coach.city && <span className="text-xs text-muted-foreground">{m.coach.city}</span>}
                        </div>
                        <p className="text-sm mt-1.5">{m.reason}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {[...m.coach.industries.slice(0, 3), ...m.coach.functions.slice(0, 3)].map((t) => (
                            <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{t}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </AppShell>
  );
}
